import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const steps = [
  { num: '1', icon: 'ri-phone-line' },
  { num: '2', icon: 'ri-search-line' },
  { num: '3', icon: 'ri-file-list-line' },
  { num: '4', icon: 'ri-tools-line' },
];

export default function Process() {
  const { t } = useTranslation('reparation');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);

  return (
    <section ref={ref} id="process" className="bg-soft-gray py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl">
            {t('process_h2')}
          </h2>
        </div>

        {/* Image banner */}
        <div
          className={`rounded-xl overflow-hidden mb-10 md:mb-14 transition-all duration-1000 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <img
            src="https://readdy.ai/api/search-image?query=Professional roofer repairing a damaged roof on a Quebec house, applying sealant and replacing missing shingles, close-up of hands working with roofing tools, safety harness visible, warm daylight, detailed repair work photography, no text visible"
            alt="Couvreur professionnel effectuant une réparation de toiture à Lévis par couvreur certifié RBQ"
            className="w-full h-48 md:h-64 object-cover object-top"
            loading="lazy"
            width="800"
            height="500"
            width="1000"
            height="350"
          />
        </div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden md:block relative">
          {/* Connector line */}
          <div className="absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-copper/20" />

          <div className="grid grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className={`text-center transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${idx * 150 + 200}ms` }}
              >
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-white border-2 border-copper shadow-sm mb-5 z-10">
                  <span className="font-manrope font-bold text-copper text-xl">{step.num}</span>
                </div>
                <h3 className="font-manrope font-bold text-slate-primary text-base mb-2">
                  {t(`process_step${idx + 1}_title`)}
                </h3>
                <p className="text-slate-primary/60 text-sm leading-relaxed px-2">
                  {t(`process_step${idx + 1}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="md:hidden space-y-8 relative">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-copper/20" />
          {steps.map((step, idx) => (
            <div
              key={idx}
              className={`relative pl-16 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${idx * 150 + 200}ms` }}
            >
              <div className="absolute left-0 top-0 w-12 h-12 rounded-full bg-white border-2 border-copper flex items-center justify-center shadow-sm">
                <span className="font-manrope font-bold text-copper text-lg">{step.num}</span>
              </div>
              <h3 className="font-manrope font-bold text-slate-primary text-base mb-1">
                {t(`process_step${idx + 1}_title`)}
              </h3>
              <p className="text-slate-primary/60 text-sm leading-relaxed">
                {t(`process_step${idx + 1}_desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}